import type { SortingState } from '@tanstack/react-table';
import { Actions } from '../../components/product-table/columns/actions-column';
import { Brand } from '../../components/product-table/columns/brand-column';
import { Department } from '../../components/product-table/columns/department-column';
import { Description } from '../../components/product-table/columns/description-column';
import { ExtendedCost } from '../../components/product-table/columns/extended-cost-column';
import { ItemCode } from '../../components/product-table/columns/item-code-column';
import { LastReceived } from '../../components/product-table/columns/last-received-column';
import { MarginPercentage } from '../../components/product-table/columns/margin-percentage-column';
import { ProductNameWithLabels } from '../../components/product-table/columns/product-name-with-labels-column';
import { QOH } from '../../components/product-table/columns/qoh-column';
import { Reorder } from '../../components/product-table/columns/reorder-column';
import { Size } from '../../components/product-table/columns/size-column';
import { SizeOunces } from '../../components/product-table/columns/size-ounces-column';
import { Type } from '../../components/product-table/columns/type-column';
import { VendorName } from '../../components/product-table/columns/vendor-name-column';
import { DepartmentFilter } from '../../components/product-table/filters/department-filter';
import { FiltersPopover } from '../../components/product-table/filters/filters-popover';
import { HideDisabledFilter } from '../../components/product-table/filters/hide-disabled-filter';
import { NotSoldInFilter } from '../../components/product-table/filters/not-sold-in-filter';
import { TotalValue } from '../../components/product-table/filters/total-value-filter';
import type { StrictColumnDef } from '../../components/tanstack-table/column';
import { ReportActions } from '../../components/tanstack-table/report-actions';
import { TanStackTable } from '../../components/tanstack-table/table';
import { useRouteRowSelection } from '../../components/tanstack-table/use-route-row-selection';
import { useTableRef } from '../../components/tanstack-table/use-table-ref';
import { PageToolbar } from '../../components/toolbar/page-toolbar';
import { SearchWithCount } from '../../components/toolbar/search-with-count';
import { ProductTableContext } from '../../contexts/product-table-context';
import type { Product, SaleStatDays } from '../../data/product';
import { type Filters, useFilteredProducts } from '../../hooks/products';
import { productReportsLayout } from '../../router';

// Display columns
const displayColumns: StrictColumnDef<Product>[] = [
  VendorName,
  ItemCode,
  ProductNameWithLabels,
  Type,
  Size,
  QOH,
  LastReceived,
  ExtendedCost,
  Reorder,
  MarginPercentage,
  Actions,
];

// Sort columns (includes hidden columns for sorting)
const allColumnsForSort: StrictColumnDef<Product>[] = [
  ...displayColumns,
  Department,
  SizeOunces,
  Brand,
  Description,
];

// Multi-column sort order
const sorting: SortingState = [
  { id: Department.id, desc: false },
  { id: SizeOunces.id, desc: true },
  { id: Type.id, desc: false },
  { id: Brand.id, desc: false },
  { id: Description.id, desc: true },
];

// Column visibility: hide the sort-only columns
const columnVisibility = {
  [Department.id]: false,
  [SizeOunces.id]: false,
  [Brand.id]: false,
  [Description.id]: false,
};

const defaultFilters: Filters = {
  minimumInventory: 1,
  notSoldIn: 180,
};

const soldInDays: SaleStatDays[] = [60, 90, 180, 365];

export function PhantomInventoryReport() {
  const products = productReportsLayout.useLoaderData();
  const { filters, setFilters, filteredProducts, baselineCount } = useFilteredProducts(
    products,
    defaultFilters
  );
  const { rowSelection, onRowSelectionChange, onRowClick } = useRouteRowSelection();
  const [table, onTableReady] = useTableRef<Product>();

  return (
    <>
      <ProductTableContext.Provider value={{ filters, setFilters, filteredProducts }}>
        <PageToolbar
          left={
            <FiltersPopover defaultFilters={defaultFilters}>
              <HideDisabledFilter />
              <DepartmentFilter products={products} />
              <NotSoldInFilter overrideSoldInDays={soldInDays} />
            </FiltersPopover>
          }
          right={
            <>
              <TotalValue />
              <SearchWithCount
                quantity={filteredProducts.length}
                total={baselineCount}
                noun="products"
              />
              <ReportActions table={table} title="Phantom Inventory" />
            </>
          }
        />
      </ProductTableContext.Provider>
      <TanStackTable
        data={filteredProducts}
        columns={allColumnsForSort}
        sorting={sorting}
        columnVisibility={columnVisibility}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        onRowClick={onRowClick}
        getRowId={(product) => product.id}
        onTableReady={onTableReady}
      />
    </>
  );
}
